import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { ArrowDown } from "../../assets/icons/navigation/arrowDown";
import BackArrow from "../../assets/icons/navigation/backArrow";
import { NextArrow } from "../../assets/icons/navigation/nextArrow";
import { SkipToFirst } from "../../assets/icons/navigation/skipToFirst";
import { SkipToLast } from "../../assets/icons/navigation/skipToLast";
import { CloseIcon } from "../../assets/icons/closeIcon";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";

export interface NumberedPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  "aria-label"?: string;
}

export interface PaginationProps extends NumberedPaginationProps {
  rowsPerPageOptions?: number[];
  limit?: number;
  onRowsPerPageChange?: (value: number) => void;
  rowsMenuPlacement?: "up" | "down";
}

const focusStyle = css`
  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }
`;

const CompactPaginationRoot = styled.nav`
  display: flex;
  justify-content: center;
  gap: ${vars.spacing.sm};
  margin-top: ${vars.spacing.lg};
`;

export const PaginationButton = styled(ButtonBase)<{ $active?: boolean }>`
  padding: var(--inzeloop-ds-spacing-6px) ${vars.spacing.md};
  border: 0;
  border-radius: ${vars.radius.xs};
  background: ${({ $active }) =>
    $active === undefined
      ? vars.color.action.primary
      : $active
        ? vars.color.action.primaryPressed
        : vars.color.action.disabled};
  color: ${vars.color.text.inverse};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  line-height: ${vars.typography.lineHeight.xs};
  cursor: pointer;

  &:disabled {
    background: ${vars.color.action.disabled};
    cursor: not-allowed;
  }

  ${focusStyle}
`;

export const NumberedPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  "aria-label": ariaLabel = "Pagination",
}: NumberedPaginationProps) => {
  const changePage = (page: number) => {
    if (page >= 1 && page <= totalPages) onPageChange(page);
  };

  return (
    <CompactPaginationRoot aria-label={ariaLabel}>
      <PaginationButton
        type="button"
        onClick={() => changePage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </PaginationButton>
      {Array.from({ length: totalPages }, (_, index) => index + 1).map(
        (page) => (
          <PaginationButton
            type="button"
            key={page}
            $active={page === currentPage}
            aria-current={page === currentPage ? "page" : undefined}
            aria-label={`Page ${page}`}
            onClick={() => changePage(page)}
          >
            {page}
          </PaginationButton>
        ),
      )}
      <PaginationButton
        type="button"
        onClick={() => changePage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </PaginationButton>
    </CompactPaginationRoot>
  );
};

const PaginationRoot = styled.nav`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 40px;
  align-items: flex-start;
  justify-content: space-between;
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.sm};
`;

const RowSelector = styled.div`
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
  padding-top: ${vars.spacing.lg};
`;

const RowsMenuRoot = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
`;

const RowsMenuTrigger = styled(ButtonBase)`
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: ${vars.radius.xs};
  background: var(--inzeloop-ds-color-background-transparent);
  color: ${vars.color.text.primary};

  ${focusStyle}
`;

const RowsMenuIcon = styled.span<{
  $open: boolean;
  $placement: "up" | "down";
}>`
  display: flex;
  padding: ${vars.spacing.xxs};
  border-radius: ${({ $placement }) =>
    $placement === "up"
      ? `${vars.radius.none} ${vars.radius.none} ${vars.radius.sm} ${vars.radius.sm}`
      : `${vars.radius.sm} ${vars.radius.sm} ${vars.radius.none} ${vars.radius.none}`};
  background: ${({ $open }) =>
    $open ? vars.color.background.surface : vars.color.background.transparent};
`;

const RowsMenu = styled.div<{ $placement: "up" | "down" }>`
  position: absolute;
  top: ${({ $placement }) => ($placement === "down" ? "97%" : "auto")};
  bottom: ${({ $placement }) => ($placement === "up" ? "97%" : "auto")};
  z-index: ${vars.zIndex.dropdown};
  display: flex;
  width: 48px;
  flex-direction: column;
  margin-right: var(--inzeloop-ds-spacing-20px);
  border-radius: ${({ $placement }) =>
    $placement === "up"
      ? `${vars.radius.sm} ${vars.radius.sm} ${vars.radius.none} ${vars.radius.none}`
      : `${vars.radius.none} ${vars.radius.none} ${vars.radius.sm} ${vars.radius.sm}`};
  background: ${vars.color.background.surface};
  box-shadow: ${vars.shadow.dropdown};
`;

const RowsMenuOption = styled(ButtonBase)<{ $selected: boolean }>`
  display: flex;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--inzeloop-ds-spacing-5px);
  margin-left: ${vars.spacing.sm};
  padding: 0;
  border: 0;
  border-radius: ${vars.radius.xs};
  background: ${({ $selected }) =>
    $selected
      ? vars.color.action.secondaryHover
      : vars.color.background.transparent};
  color: ${vars.color.text.primary};
  font: inherit;
  cursor: default;

  &:hover {
    background: ${vars.color.action.primaryHover};
    color: ${vars.color.text.inverse};
  }

  ${focusStyle}
`;

const PageControls = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1 0 0;
  align-items: flex-start;
  justify-content: center;
  padding-top: ${vars.spacing.lg};
  padding-right: calc(var(--inzeloop-ds-spacing-2xl) * 3);
`;

const PageControlGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${vars.spacing.xs};
  padding-top: ${vars.borderWidth.thin};
  padding-right: ${vars.borderWidth.thin};
`;

const PageControl = styled(ButtonBase)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: ${vars.radius.xs};
  background: var(--inzeloop-ds-color-background-transparent);
  color: ${vars.color.text.primary};

  &:disabled {
    color: ${vars.color.border.medium};
  }

  ${focusStyle}
`;

const PageInfo = styled.span`
  display: flex;
  gap: ${vars.spacing.sm};
  padding-inline: calc(
    var(--inzeloop-ds-spacing-20px) - var(--inzeloop-ds-spacing-1px)
  );
`;

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  rowsPerPageOptions = [5, 10, 20, 25, 50, 100],
  limit = 10,
  onRowsPerPageChange = () => {},
  rowsMenuPlacement = "down",
  "aria-label": ariaLabel = "Pagination controls",
}: PaginationProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = `pagination-rows-${useId().replace(/:/g, "")}`;

  const changePage = (page: number) => {
    if (page >= 1 && page <= totalPages) onPageChange(page);
  };

  const closeMenu = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const openMenu = (focusIndex?: number) => {
    setOpen(true);
    window.requestAnimationFrame(() => {
      const selectedIndex = rowsPerPageOptions.indexOf(limit);
      optionRefs.current[
        focusIndex ?? (selectedIndex >= 0 ? selectedIndex : 0)
      ]?.focus();
    });
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closeMenu();
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const handleOptionKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number | undefined;
    if (event.key === "ArrowDown") {
      nextIndex = (index + 1) % rowsPerPageOptions.length;
    } else if (event.key === "ArrowUp") {
      nextIndex =
        (index - 1 + rowsPerPageOptions.length) % rowsPerPageOptions.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = rowsPerPageOptions.length - 1;
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeMenu(true);
      return;
    }

    if (nextIndex !== undefined) {
      event.preventDefault();
      optionRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <PaginationRoot ref={rootRef} aria-label={ariaLabel}>
      <RowSelector>
        <span>Show</span>
        <span>{limit}</span>
        <RowsMenuRoot>
          <RowsMenuTrigger
            ref={triggerRef}
            type="button"
            aria-label="Items per page"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            onClick={() => (open ? closeMenu() : openMenu())}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                openMenu(
                  event.key === "ArrowDown" ? 0 : rowsPerPageOptions.length - 1,
                );
              } else if (event.key === "Escape" && open) {
                event.preventDefault();
                closeMenu(true);
              }
            }}
          >
            <RowsMenuIcon $open={open} $placement={rowsMenuPlacement}>
              {open ? (
                <CloseIcon aria-hidden="true" focusable="false" />
              ) : (
                <ArrowDown aria-hidden="true" focusable="false" />
              )}
            </RowsMenuIcon>
          </RowsMenuTrigger>
          {open && (
            <RowsMenu
              id={menuId}
              role="listbox"
              aria-label="Rows per page"
              $placement={rowsMenuPlacement}
              data-placement={rowsMenuPlacement}
            >
              {rowsPerPageOptions.map((option, index) => (
                <RowsMenuOption
                  ref={(element) => {
                    optionRefs.current[index] = element;
                  }}
                  type="button"
                  role="option"
                  key={option}
                  aria-selected={option === limit}
                  $selected={option === limit}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                  onClick={() => {
                    onRowsPerPageChange(option);
                    closeMenu(true);
                  }}
                >
                  {option}
                </RowsMenuOption>
              ))}
            </RowsMenu>
          )}
        </RowsMenuRoot>
        <span>rows</span>
      </RowSelector>

      <PageControls>
        <PageControlGroup>
          <PageControl
            type="button"
            aria-label="First Page"
            onClick={() => changePage(1)}
            disabled={currentPage === 1}
          >
            <SkipToFirst aria-hidden="true" focusable="false" />
          </PageControl>
          <PageControl
            type="button"
            aria-label="Previous Page"
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <BackArrow aria-hidden="true" focusable="false" />
          </PageControl>
        </PageControlGroup>

        <PageInfo>
          {currentPage} of {totalPages}
        </PageInfo>

        <PageControlGroup>
          <PageControl
            type="button"
            aria-label="Next Page"
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <NextArrow aria-hidden="true" focusable="false" />
          </PageControl>
          <PageControl
            type="button"
            aria-label="Last Page"
            onClick={() => changePage(totalPages)}
            disabled={currentPage === totalPages}
          >
            <SkipToLast aria-hidden="true" focusable="false" />
          </PageControl>
        </PageControlGroup>
      </PageControls>
    </PaginationRoot>
  );
};
